import axios, { AxiosError, AxiosRequestConfig } from 'axios';

type LogoutHandler = () => void | Promise<void>;

let logoutHandler: LogoutHandler | null = null;
let axiosConfigured = false;

/**
 * Register a logout handler that will be called when authentication fails (401/403)
 * This should be called from AuthContext when the logout function is available
 */
export const registerLogoutHandler = (handler: LogoutHandler) => {
    logoutHandler = handler;
};

/**
 * Unregister the logout handler (useful for cleanup)
 */
export const unregisterLogoutHandler = () => {
    logoutHandler = null;
};

/**
 * Handle authentication errors by calling the registered logout handler
 */
const handleAuthError = async (error: AxiosError) => {
    const status = error.response?.status;
    
    // Handle 401 (Unauthorized) and 403 (Forbidden) errors
    if (status === 401 || status === 403) {
        console.warn('[axios] Authentication error detected, logging out user', { status, url: error.config?.url });
        
        if (logoutHandler) {
            try {
                await logoutHandler();
            } catch (err) {
                console.error('[axios] Error during logout handler execution', err);
            }
        } else {
            console.warn('[axios] No logout handler registered, cannot handle auth error');
        }
        
        // Return a rejected promise with a user-friendly error message
        return Promise.reject(new Error('Autentikointi epäonnistui. Kirjaudu sisään uudelleen.'));
    }
    
    // For other errors, pass them through unchanged
    return Promise.reject(error);
};

/**
 * Create a configured axios instance with authentication error handling
 */
export const createAxiosInstance = (config?: AxiosRequestConfig) => {
    const instance = axios.create(config);
    
    // Add response interceptor to handle authentication errors
    instance.interceptors.response.use(
        (response) => response, // Pass through successful responses
        handleAuthError // Handle errors
    );
    
    return instance;
};

/**
 * Configure the default axios instance with authentication error handling
 * This affects all axios requests that don't use a custom instance
 * Safe to call multiple times - only configures once
 */
export const configureAxios = () => {
    if (axiosConfigured) {
        return;
    }
    axios.interceptors.response.use(
        (response) => response, // Pass through successful responses
        handleAuthError // Handle errors
    );
    axiosConfigured = true;
};

